import axios from "axios"
import {
    BANK_TRANSACTIONS_CANCEL_IDENTIFY,
    BANK_TRANSACTIONS_CHANGE,
    BANK_TRANSACTIONS_CHANGE_TYPE,
    BANK_TRANSACTIONS_ENDPOINT,
    BANK_TRANSACTIONS_ENDPOINT_MOVEMENT_TYPES,
    BANK_TRANSACTIONS_IDENTIFY,
    BANK_TRANSACTIONS_IDENTIFY_MULTIPLE
} from "../../../../../../../../utility/SourceAPI"
import { getDateToFilterAPI } from "../../../../../../../../utility/formats/formattedDate"

/**
 * Única capa que habla con el backend de transacciones bancarias.
 * Devuelve siempre `response.data`, nunca el envoltorio de axios, y deja que el
 * error suba tal cual: quien decide qué hacer con él es el thunk (rejectWithValue).
 */

const multipartConfig = {
    headers: {
        'Content-Type': 'multipart/form-data;boundary=boundary'
    }
}

//Repite la llave por cada valor de un arreglo (company_rfc=a&company_rfc=b); con `params`
//axios usaría el formato con corchetes (company_rfc[]=a)
const buildQueryString = (params) => Object.keys(params)
    .map(key => {
        const values = Array.isArray(params[key]) ? params[key] : [params[key]]
        return values.map(value => `${key}=${encodeURIComponent(value)}`).join('&')
    })
    .join('&')

export const fetchBankTransactions = async ({ account, rangeDates }) => {
    const dates = rangeDates !== undefined ? `&${getDateToFilterAPI(rangeDates)}` : ''
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`?bank_account_id=${account}${dates}`))
    return response.data
}

export const fetchBankTransactionsMXNTraditional = async ({ account, rangeDates }) => {
    const dates = rangeDates !== undefined ? `&${getDateToFilterAPI(rangeDates)}` : ''
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`?bank_account_id=${account}&department=Bancos-Toluca${dates}`))
    return response.data
}

export const fetchBankTransactionsByMovement = async (movementTypeId) => {
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`?movement_type=${movementTypeId}`))
    return response.data
}

export const fetchMovementTypes = async () => {
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT_MOVEMENT_TYPES)
    return response.data
}

export const fetchBankTransactionsToInvoice = async (data) => {
    let params = {}
    if (data.id_transaction) {
        params = { ...params, id_transaction: data.id_transaction }
    }
    if (data.amount > 0) {
        params = { ...params, amount: data.amount }
    }
    if (data.company_rfc) {
        params = { ...params, company_rfc: data.company_rfc }
    }
    if (data.bank_account_id) {
        params = { ...params, bank_account_id: data.bank_account_id }
    }
    const queryString = buildQueryString(params)
    const response = await axios.get(`${BANK_TRANSACTIONS_ENDPOINT}get_to_invoces/${queryString ? `?${queryString}` : ''}`)
    return response.data
}

export const fetchReportTransactions = async ({ rangeDates, charge, deposit }) => {
    const dates = rangeDates !== undefined ? `${getDateToFilterAPI(rangeDates)}&` : ''
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`group-mounth/?${dates}charge=${charge}&deposit=${deposit}`))
    return response.data
}

export const fetchFilterBankTransactions = async (data) => {
    let params = {}
    if (data.companies.length > 0) {
        params = { ...params, company_rfc: data.companies }
    }
    if (data.banks.length > 0) {
        params = { ...params, bank_type: data.banks }
    }
    if (data.intermediary.length > 0) {
        params = { ...params, intermediary_id: data.intermediary }
    }
    if (data.type.length > 0) {
        params = { ...params, is_out: data.type }
    }
    if (data.movement_type.length > 0) {
        params = { ...params, movement_type: data.movement_type }
    }

    const queryString = buildQueryString(params)
    const url = `${BANK_TRANSACTIONS_ENDPOINT.concat('resume/')}${queryString ? `?${queryString}` : ''}`
    const response = await axios.get(`${url.concat(`&${getDateToFilterAPI(data.rangeDates)}${data?.fullAccess !== null ? `&department=${data.fullAccess}` : ''}&currency=${data?.currency}${data.client_name !== '' ? `&client_name=${data.client}` : ''}`)}`, { timeout: 180000 })
    return response.data
}

export const fetchBankTransactionsIntermediary = async ({ id, rangeDates }) => {
    const dates = rangeDates !== undefined ? `&${getDateToFilterAPI(rangeDates)}` : ''
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`?intermediary_id=${id}${dates}&is_out=false`))
    return response.data
}

export const fetchAllBankTransactionsIntermediary = async ({ id, rangeDates }) => {
    const response = await axios.get(rangeDates !== undefined ? BANK_TRANSACTIONS_ENDPOINT.concat(`?intermediary_id=${id}&is_out=false&is_identified=true&order_by_identified=true`).concat(`&${getDateToFilterAPI(rangeDates)}`) : BANK_TRANSACTIONS_ENDPOINT.concat(`?intermediary_id=${id}&is_out=false&order_by_identified=true`))
    return response.data
}

export const fetchBankTransactionsUnidentified = async (currency, rangeDates) => {
    const response = await axios.get(rangeDates !== undefined ? BANK_TRANSACTIONS_ENDPOINT.concat(`?get_all_unknown=false&is_identified=false&is_out=false&currency=${currency}`).concat(`&${getDateToFilterAPI(rangeDates)}`) : BANK_TRANSACTIONS_ENDPOINT.concat(`?get_all_unknown=true&is_identified=false&is_out=false&currency=${currency}`), { timeout: 120000 })
    return response.data
}

export const fetchBankTransactionsIdentified = async (currency, rangeDates) => {
    const dates = rangeDates !== undefined ? `&${getDateToFilterAPI(rangeDates)}` : ''
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`?is_identified=true&is_out=false&order_by_identified=true&currency=${currency}${dates}`), { timeout: 120000 })
    return response.data
}

export const fetchBankTransactionsSBC = async (rangeDates) => {
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`subject_to_collection/${rangeDates !== undefined ? `?${getDateToFilterAPI(rangeDates)}` : ''}`))
    return response.data
}

export const fetchBankTransactionsSBCByAccount = async ({ id, rangeDates }) => {
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`subject_to_collection_by_bank_account/${id}${rangeDates.end !== undefined ? `?${getDateToFilterAPI(rangeDates)}` : ''}`))
    return response.data
}

export const fetchBankTransactionsToConfirm = async (currency) => {
    const response = await axios.get(BANK_TRANSACTIONS_ENDPOINT.concat(`to-confirm/?currency=${currency}`))
    return response.data
}

export const validateCheckSBC = async (id) => {
    const response = await axios.put(BANK_TRANSACTIONS_ENDPOINT.concat(`is_validated/${id}`))
    return response.data
}

export const createBankTransaction = async (bankTransaction) => {
    const response = await axios.post(BANK_TRANSACTIONS_ENDPOINT, bankTransaction, multipartConfig)
    return response.data
}

export const updateBankTransactionTimestamp = async (id, bankTransaction) => {
    const response = await axios.put(BANK_TRANSACTIONS_ENDPOINT.concat(`change_timestamp/${id}`), bankTransaction)
    return response.data
}

export const updateBankTransactionConcept = async (bankTransaction) => {
    const response = await axios.put(BANK_TRANSACTIONS_ENDPOINT.concat(`update-concept/`), bankTransaction)
    return response.data
}

export const identifyBankTransaction = async (id, bankTransaction) => {
    const response = await axios.put(BANK_TRANSACTIONS_IDENTIFY.concat(id), bankTransaction)
    return response.data
}

export const identifyBankTransactions = async (bankTransactions) => {
    const response = await axios.put(BANK_TRANSACTIONS_IDENTIFY_MULTIPLE, bankTransactions)
    return response.data
}

export const changeBankTransaction = async (id, bankTransaction) => {
    const response = await axios.put(BANK_TRANSACTIONS_CHANGE.concat(id), bankTransaction)
    return response.data
}

export const changeTransactionType = async (id, bankTransaction) => {
    const response = await axios.put(BANK_TRANSACTIONS_CHANGE_TYPE.concat(id), bankTransaction)
    return response.data
}

export const releaseTransactions = async (data) => {
    const response = await axios.put(BANK_TRANSACTIONS_ENDPOINT.concat(`permission_to_identify/`), data)
    return response.data
}

export const confirmBankTransaction = async (bankTransaction) => {
    const response = await axios.put(BANK_TRANSACTIONS_ENDPOINT.concat(`permission_to_delete/`), bankTransaction)
    return response.data
}

export const confirmDevolutionFondeo = async (transactionId) => {
    const response = await axios.delete(BANK_TRANSACTIONS_ENDPOINT.concat(`credit_anchoring_returned/${transactionId}`))
    return response.data
}

export const confirmChequeSBC = async (id, statusCsb) => {
    const response = await axios.put(BANK_TRANSACTIONS_ENDPOINT.concat(`status_csb/${id}`), statusCsb)
    return response.data
}

export const cancelIdentifyBankTransaction = async (id) => {
    const response = await axios.put(BANK_TRANSACTIONS_CANCEL_IDENTIFY.concat(id))
    return response.data
}

export const deleteBankTransaction = async (transactionId) => {
    const response = await axios.delete(BANK_TRANSACTIONS_ENDPOINT.concat(`?transaction_id=${transactionId}`))
    return response.data
}

export const linkCashCredit = async (transactionId, userActivityId) => {
    const response = await axios.put(BANK_TRANSACTIONS_ENDPOINT.concat(`linked-cash-credit/${transactionId}?user_activity_id=${userActivityId}`))
    return response.data
}
